import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  useTheme,
  alpha,
  Avatar,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";

// Deterministic color from a string — gives each sender a consistent avatar color
const stringToColor = (str = "") => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const colors = [
    "#E91E63", "#9C27B0", "#3F51B5", "#2196F3",
    "#00BCD4", "#009688", "#4CAF50", "#FF9800",
  ];
  return colors[Math.abs(hash) % colors.length];
};

function ChatArea({ roomCode, playerUsername, socket, messages }) {
  const [msg, setMsg] = useState("");
  const endRef = useRef(null);
  const inputRef = useRef(null);
  const theme = useTheme();

  // Scroll to bottom on new messages
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = () => {
    const text = msg.trim();
    if (!text) return;
    socket.emit("sendMessage", { roomCode, message: text });
    setMsg("");
    // Keep focus on input after sending
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (ts) => {
    if (!ts) return "";
    return new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        // Critical: use 100% of parent height, not min-height tricks
        height: "100%",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2,
          py: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: alpha(theme.palette.background.paper, 0.9),
          backdropFilter: "blur(8px)",
          flexShrink: 0,
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: "0.8rem" }}>
          Room Chat
        </Typography>
      </Box>

      {/* Messages — scrollable, takes all available space */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          px: 1.5,
          py: 1,
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
        }}
      >
        {messages.length === 0 && (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mt: 4,
            }}
          >
            <Typography
              variant="body2"
              sx={{ opacity: 0.3, fontStyle: "italic", textAlign: "center", fontSize: "0.8rem" }}
            >
              No messages yet.{"\n"}Say hello!
            </Typography>
          </Box>
        )}

        {messages.map((m, i) => {
          const isOwn = m.sender === playerUsername;
          const showAvatar =
            !isOwn &&
            (i === 0 || messages[i - 1]?.sender !== m.sender);
          const showName =
            !isOwn &&
            (i === 0 || messages[i - 1]?.sender !== m.sender);
          const avatarColor = stringToColor(m.sender);

          return (
            <Box
              key={i}
              sx={{
                display: "flex",
                flexDirection: isOwn ? "row-reverse" : "row",
                alignItems: "flex-end",
                gap: 0.75,
                // Tighter spacing if same sender
                mb: i < messages.length - 1 && messages[i + 1]?.sender === m.sender ? 0.25 : 0.75,
              }}
            >
              {/* Avatar — only shown on first message of a run */}
              {!isOwn ? (
                <Avatar
                  sx={{
                    width: 26,
                    height: 26,
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    bgcolor: avatarColor,
                    color: "#fff",
                    flexShrink: 0,
                    visibility: showAvatar ? "visible" : "hidden",
                  }}
                >
                  {m.sender?.charAt(0)?.toUpperCase() || "?"}
                </Avatar>
              ) : (
                // Spacer on own side so message doesn't stretch to edge
                <Box sx={{ width: 26, flexShrink: 0 }} />
              )}

              <Box sx={{ maxWidth: "78%", display: "flex", flexDirection: "column", alignItems: isOwn ? "flex-end" : "flex-start" }}>
                {/* Sender name — only on first in a group */}
                {showName && (
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: avatarColor,
                      fontSize: "0.7rem",
                      mb: 0.2,
                      ml: 0.5,
                    }}
                  >
                    {m.sender}
                  </Typography>
                )}

                {/* Bubble */}
                <Box
                  sx={{
                    backgroundColor: isOwn
                      ? alpha(theme.palette.primary.main, 0.18)
                      : alpha(theme.palette.background.default, 0.85),
                    borderRadius: isOwn
                      ? "16px 16px 4px 16px"
                      : "16px 16px 16px 4px",
                    px: 1.5,
                    py: 0.75,
                    border: `1px solid ${isOwn
                      ? alpha(theme.palette.primary.main, 0.2)
                      : theme.palette.divider
                      }`,
                    backdropFilter: "blur(4px)",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ wordBreak: "break-word", lineHeight: 1.45, fontSize: "0.85rem" }}
                  >
                    {m.text}
                  </Typography>
                </Box>

                {/* Timestamp */}
                <Typography
                  variant="caption"
                  sx={{
                    opacity: 0.4,
                    fontSize: "0.6rem",
                    mt: 0.2,
                    mx: 0.5,
                  }}
                >
                  {formatTime(m.timestamp)}
                </Typography>
              </Box>
            </Box>
          );
        })}

        <div ref={endRef} />
      </Box>

      {/* Input bar — sticky at bottom, keyboard-aware via flex */}
      <Box
        sx={{
          flexShrink: 0,
          px: 1.5,
          py: 1,
          borderTop: `1px solid ${theme.palette.divider}`,
          display: "flex",
          gap: 1,
          alignItems: "center",
          backgroundColor: alpha(theme.palette.background.paper, 0.92),
          backdropFilter: "blur(12px)",
          // Safe-area for iPhone home bar
          paddingBottom: "max(8px, env(safe-area-inset-bottom))",
        }}
      >
        <TextField
          inputRef={inputRef}
          fullWidth
          placeholder="Message..."
          value={msg}
          onChange={(e) => setMsg(e.target.value)}
          onKeyDown={handleKeyDown}
          size="small"
          multiline
          maxRows={3}
          inputProps={{ maxLength: 500 }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 4,
              fontSize: "0.85rem",
              backgroundColor: alpha(theme.palette.background.default, 0.5),
              "& fieldset": { borderColor: "transparent" },
              "&:hover fieldset": { borderColor: theme.palette.divider },
              "&.Mui-focused fieldset": {
                borderColor: theme.palette.primary.main,
                borderWidth: 1,
              },
            },
          }}
        />
        <IconButton
          onClick={handleSend}
          disabled={!msg.trim()}
          size="small"
          sx={{
            width: 36,
            height: 36,
            flexShrink: 0,
            backgroundColor: msg.trim()
              ? alpha(theme.palette.primary.main, 0.15)
              : "transparent",
            color: theme.palette.primary.main,
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: alpha(theme.palette.primary.main, 0.25),
              transform: "scale(1.05)",
            },
            "&:disabled": { opacity: 0.3 },
          }}
        >
          <SendIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
    </Box>
  );
}

export default ChatArea;
